/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {GroupTypeEntity} from '@ion/core/src/types/entities';
import EntityPropertyChooser, {
  EntityPropertyChooserClientProps,
  GroupPropertyKind
} from './EntityPropertyChooser';

export interface EntityTypeChooserProps extends EntityPropertyChooserClientProps {
  entity: GroupTypeEntity
};

/**
 * @EntityTypeChooser
 *
 * The EntityTypeChooser provides a list of user defined, account specific entity
 * types for display within a dropdown list, allowing the user to select a type
 * from the list for subsequent persistence to the entity.
 *
 * ```typescript
 *
 *     import {EntityTypeChooser} from '@ion/dls/src/editors/';
 *     import {GroupTypeEntity} from '@ion/core/src/types/entities';
 *
 *     // add an entity type chooser for assets ...
 *     <EntityTypeChooser entity={GroupTypeEntity.asset}
 *       defaultValue={asset.assetType}
 *       isLoading={isLoading}
 *       onChange(setType) />

 *     // add an entity type chooser for operators ...
 *     <EntityTypeChooser entity={GroupTypeEntity.operator}
 *       defaultValue={operator.operatorType}
 *       isLoading={isLoading}
 *       onChange(setType) />
 *
 *     // provide a list of types (allows for bypassing the API ...)
 *     <EntityTypeChooser entity={GroupTypeEntity.asset}
 *       values={operatorTypes.value} />
 *
 *     // add a read-only entity type chooser for assets and operators ...
 *     <EntityTypeChooser entity={GroupTypeEntity.asset}
 *       defaultValue={operator.operatorType}
 *       readOnly />
 *     <EntityTypeChooser entity={GroupTypeEntity.operator}
 *       isLoading={isLoading}
 *       readOnly />
 *
 * @entity            Required, specifies the context in which the component is displaying
 *                    entity types (e.g. "Asset Types", "Operator Types").
 * @isLoading         Required, when true, indicates that the component is to display its
 *                    loading state, and when changed to false, indicates that the items
 *                    within the dropdown are to be updated to reflect the latest entity
 *                    types from the GroupProperties API.
 * @defaultValue      Allows for providing a default selected value from the list.
 * @readOnly          When true, the component will be displayed in read-only mode;
 *                    that is, disabled. Defaults to false.
 * @onChange          Callback prop which will be invoked with the seleted entity type.
 * @values            Optional list of entity types to be used. When provided, the
 *                    GroupProperties API will be bypassed and only the items provided
 *                    will be displayed.
 * @includeGroupName  When true, indicates that the account for which the entity types
 *                    have been provided should be displayed within the label.
 * @classes           Optional classes to apply to the wrapping element.
 */
export const EntityTypeChooser = ({translation = 'common:editors.entityType', ...rest}: EntityPropertyChooserClientProps) => (
  <EntityPropertyChooser {...rest}
    kind={GroupPropertyKind.Types}
    translation={translation} />
);
export default EntityTypeChooser;
